/// <reference lib="dom"/>
/** @jsx hh */
/** @jsxFrag ChiFragment */
import { ChiFragment, hh } from "@chi/chi";
import type { ChiNode } from "@chi/chi/hooks";
import { At, WbaxRoot } from "@chi/wbax";
import { ParamDictToProps } from "@utils/params";
import {
  type ActionHandlers,
  Dispatcher,
  type DispatcherOptions,
  type Message,
} from "@utils/dispatch";
import { type SynthDef, SynthOpts } from "@/synth/synth.tsx";
import { Drums808 } from "@/synth/drums808.tsx";
import { DrumsJazz } from "@/synth/drumsJazz.tsx";
import {
  NightVisionSynth,
  NoiseToneSynth,
  OceanSynth,
  ShushSynth,
  WindSynth,
} from "@/synth/ambient.tsx";
import { MultiOscSynth, ToneSynth } from "@/synth/tones.tsx";
import {
  EffectChain,
  type EffectDef,
  EffectWorklets,
  LiveEffects,
  type LiveEffectType,
} from "@/synth/effects.tsx";
import {
  type FxSlot,
  FxSlotAdd,
  FxSlotBypass,
  FxSlotMove,
  FxSlotOpts,
  FxSlotRemove,
} from "@/synth/fxChain.tsx";

//#region registry
const Prefixed = (prefix: string, defs: Record<string, SynthDef<any>>) =>
  Object.fromEntries(Object.entries(defs).map(([k, def]) => [`${prefix}/${k}`, def]));

export const SampleGenSynths: Record<string, SynthDef<any>> = {
  wind: WindSynth,
  shush: ShushSynth,
  ocean: OceanSynth,
  noiseTone: NoiseToneSynth,
  nightVision: NightVisionSynth,
  tone: ToneSynth,
  multiOsc: MultiOscSynth,
  ...Prefixed("808", Drums808),
  ...Prefixed("jazz", DrumsJazz),
};

export const SampleGenSynthNames = Object.keys(SampleGenSynths);

export function SampleGenSynthDefaults(synth: string): Record<string, unknown> {
  const def = SampleGenSynths[synth];
  return def ? ParamDictToProps(def.params) : {};
}
//#endregion

//#region state
export const SampleGenTimingTypes = ["once", "at", "repeat"] as const;
export type SampleGenTimingType = (typeof SampleGenTimingTypes)[number];

export type SampleGenTiming =
  | { type: "once"; at: number }
  | { type: "at"; at: number[] }
  | { type: "repeat"; start: number; n: number; every: number };

export interface SampleGenLayer {
  id: string;
  synth: string;
  opts: Record<string, unknown>;
  timing: SampleGenTiming;
  stop: number | null;
  gain: number;
  mute: boolean;
  fx: FxSlot[];
  bed: boolean;
}

export interface SampleGenState {
  length: number;
  sampleRate: number;
  gain: number;
  layers: SampleGenLayer[];
  fx: FxSlot[];
}

export const SampleGenDefaultState: SampleGenState = {
  length: 5,
  sampleRate: 44100,
  gain: 1,
  layers: [],
  fx: [],
};

export function SampleGenTimingDefaults(type: SampleGenTimingType): SampleGenTiming {
  if (type === "at") return { type, at: [0] };
  if (type === "repeat") return { type, start: 0, n: 2, every: 1 };
  return { type, at: 0 };
}

function NextLayerId(layers: SampleGenLayer[], synth: string) {
  const base = synth.replace(/[^a-zA-Z0-9]+/g, "-");
  let n = 1;
  while (layers.some((l) => l.id === `${base}-${n}`)) n++;
  return `${base}-${n}`;
}

export function SampleGenNewLayer(
  layers: SampleGenLayer[],
  synth: string,
  layer: Partial<Omit<SampleGenLayer, "id" | "synth">> = {},
): SampleGenLayer {
  return {
    id: NextLayerId(layers, synth),
    synth,
    timing: { type: "once", at: 0 },
    stop: null,
    gain: 1,
    mute: false,
    fx: [],
    bed: false,
    ...layer,
    opts: { ...SampleGenSynthDefaults(synth), ...layer.opts },
  };
}

export function SampleGenLayerDuration(layer: SampleGenLayer): number {
  const def = SampleGenSynths[layer.synth];
  return def ? def.duration(SynthOpts(def, layer.opts)) : 0;
}

export function SampleGenLayerTimes(layer: SampleGenLayer, length: number): number[] {
  if (layer.bed) {
    const every = Math.max(SampleGenLayerDuration(layer), 0.05);
    return Array.from({ length: Math.ceil(length / every) }, (_, i) => i * every);
  }
  const t = layer.timing;
  if (t.type === "once") return [t.at];
  if (t.type === "at") return [...t.at];
  return Array.from({ length: Math.max(0, Math.floor(t.n)) }, (_, i) => t.start + i * t.every);
}

export function SampleGenFxTypes(state: SampleGenState): LiveEffectType[] {
  const slots = [...state.fx, ...state.layers.filter((l) => !l.mute).flatMap((l) => l.fx)];
  return [...new Set(slots.filter((s) => !s.bypassed).map((s) => s.type))];
}
//#endregion

//#region tree
const CutFade = 0.01;

export interface SampleGenTreeOptions {
  assets?: Record<string, AudioBuffer>;
  ready?: (slot: FxSlot) => boolean;
}

interface SampleGenVoiceProps {
  layer: SampleGenLayer;
  length: number;
  options: SampleGenTreeOptions;
}
const SampleGenVoice = (props: SampleGenVoiceProps) => {
  const { layer, length, options } = props;
  const def = SampleGenSynths[layer.synth];
  const opts = SynthOpts(def, layer.opts);
  const stop = layer.bed ? null : layer.stop;
  const slots = layer.fx.filter((s) => options.ready?.(s) ?? true);
  return (
    <gain gain={layer.gain}>
      <EffectChain slots={slots} assets={options.assets}>
        {SampleGenLayerTimes(layer, length).map((t, i) => (
          <At key={i} t={t}>
            {stop
              ? (
                <gain
                  gain={[
                    { set: 1, at: 0 },
                    { set: 1, at: Math.max(0, stop - CutFade) },
                    { linear: 0, at: stop },
                  ]}
                >
                  {hh(def.Component, opts)}
                </gain>
              )
              : hh(def.Component, opts)}
          </At>
        ))}
      </EffectChain>
    </gain>
  );
};

export function SampleGenTree(state: SampleGenState, options: SampleGenTreeOptions = {}): ChiNode {
  const slots = state.fx.filter((s) => options.ready?.(s) ?? true);
  return (
    <gain gain={state.gain}>
      <EffectChain slots={slots} assets={options.assets}>
        {state.layers.filter((l) => !l.mute && SampleGenSynths[l.synth]).map((layer) => (
          <SampleGenVoice key={layer.id} layer={layer} length={state.length} options={options} />
        ))}
      </EffectChain>
    </gain>
  );
}
//#endregion

//#region render
export interface RenderSampleGenOptions {
  base?: string;
}

async function LoadSampleGenAssets(
  ctx: BaseAudioContext,
  state: SampleGenState,
  base: string,
): Promise<Record<string, AudioBuffer>> {
  const slots = [...state.fx, ...state.layers.flatMap((l) => l.fx)];
  const paths = new Map<string, string>();
  for (const slot of slots) {
    const def = LiveEffects[slot.type] as EffectDef;
    if (!def.assets || slot.bypassed) continue;
    const path = String(slot.opts.impulsePath ?? Object.keys(def.assets)[0]);
    paths.set(path, base + (def.assets[path] ?? path));
  }
  const loaded = await Promise.all(
    [...paths].map(async ([path, url]) => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`${url}: ${res.status} ${res.statusText}`);
      return [path, await ctx.decodeAudioData(await res.arrayBuffer())] as const;
    }),
  );
  return Object.fromEntries(loaded);
}

export async function RenderSampleGen(
  state: SampleGenState,
  options: RenderSampleGenOptions = {},
): Promise<AudioBuffer> {
  const base = options.base ?? "";
  const ctx = new OfflineAudioContext(
    1,
    Math.max(1, Math.ceil(state.length * state.sampleRate)),
    state.sampleRate,
  );
  await Promise.all(
    EffectWorklets(SampleGenFxTypes(state)).map((url) => ctx.audioWorklet.addModule(base + url)),
  );
  const assets = await LoadSampleGenAssets(ctx, state, base);
  const root = new WbaxRoot({ ctx, time: 0 });
  try {
    root.render(SampleGenTree(state, { assets }));
    return await ctx.startRendering();
  } finally {
    root.dispose();
  }
}
//#endregion

//#region dispatch
export interface SampleGenActions {
  set: { state: SampleGenState };
  length: { length: number };
  gain: { gain: number };
  "layer/add": { synth: string };
  "layer/remove": { id: string };
  "layer/move": { id: string; by: number };
  "layer/synth": { id: string; synth: string };
  "layer/opts": { id: string; opts: Record<string, unknown> };
  "layer/timing": { id: string; timing: SampleGenTiming };
  "layer/stop": { id: string; stop: number | null };
  "layer/gain": { id: string; gain: number };
  "layer/mute": { id: string };
  "layer/bed": { id: string };
  "layer/fx/add": { id: string; type: LiveEffectType };
  "layer/fx/remove": { id: string; slot: string };
  "layer/fx/move": { id: string; slot: string; by: number };
  "layer/fx/bypass": { id: string; slot: string };
  "layer/fx/opts": { id: string; slot: string; opts: Record<string, unknown> };
  "fx/add": { type: LiveEffectType };
  "fx/remove": { slot: string };
  "fx/move": { slot: string; by: number };
  "fx/bypass": { slot: string };
  "fx/opts": { slot: string; opts: Record<string, unknown> };
}

export type SampleGenMessage = Message<SampleGenActions>;
type Handlers = ActionHandlers<SampleGenActions, SampleGenState>;

const MaxLength = 600;
const GainMax = 4;

function Clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function MapLayer(
  state: SampleGenState,
  id: string,
  fn: (layer: SampleGenLayer) => SampleGenLayer,
): SampleGenState {
  return { ...state, layers: state.layers.map((l) => (l.id === id ? fn(l) : l)) };
}

function MoveLayer(layers: SampleGenLayer[], id: string, by: number) {
  const from = layers.findIndex((l) => l.id === id);
  const to = Clamp(from + by, 0, layers.length - 1);
  if (from < 0 || from === to) return layers;
  const next = [...layers];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}
//#endregion

export interface SampleGenProps {
  initial?: Partial<SampleGenState>;
  dispatcher?: DispatcherOptions;
}

export class SampleGen extends Dispatcher<SampleGenActions, SampleGenState, SampleGenProps> {
  protected readonly handlers: Handlers = {
    set: ({ payload }) => payload.state,
    length: ({ state, payload }) => ({ ...state, length: Clamp(payload.length, 0.1, MaxLength) }),
    gain: ({ state, payload }) => ({ ...state, gain: Clamp(payload.gain, 0, GainMax) }),

    "layer/add": ({ state, payload }) => ({
      ...state,
      layers: [...state.layers, SampleGenNewLayer(state.layers, payload.synth)],
    }),
    "layer/remove": ({ state, payload }) => ({
      ...state,
      layers: state.layers.filter((l) => l.id !== payload.id),
    }),
    "layer/move": ({ state, payload }) => ({
      ...state,
      layers: MoveLayer(state.layers, payload.id, payload.by),
    }),
    "layer/synth": ({ state, payload }) =>
      MapLayer(state, payload.id, (l) => ({
        ...l,
        synth: payload.synth,
        opts: SampleGenSynthDefaults(payload.synth),
      })),
    "layer/opts": ({ state, payload }) =>
      MapLayer(state, payload.id, (l) => ({ ...l, opts: { ...l.opts, ...payload.opts } })),
    "layer/timing": ({ state, payload }) =>
      MapLayer(state, payload.id, (l) => ({ ...l, timing: payload.timing })),
    "layer/stop": ({ state, payload }) =>
      MapLayer(state, payload.id, (l) => ({
        ...l,
        stop: payload.stop === null ? null : Math.max(CutFade, payload.stop),
      })),
    "layer/gain": ({ state, payload }) =>
      MapLayer(state, payload.id, (l) => ({ ...l, gain: Clamp(payload.gain, 0, GainMax) })),
    "layer/mute": ({ state, payload }) =>
      MapLayer(state, payload.id, (l) => ({ ...l, mute: !l.mute })),
    "layer/bed": ({ state, payload }) =>
      MapLayer(state, payload.id, (l) => ({ ...l, bed: !l.bed })),

    "layer/fx/add": ({ state, payload }) =>
      MapLayer(state, payload.id, (l) => ({ ...l, fx: FxSlotAdd(l.fx, payload.type) })),
    "layer/fx/remove": ({ state, payload }) =>
      MapLayer(state, payload.id, (l) => ({ ...l, fx: FxSlotRemove(l.fx, payload.slot) })),
    "layer/fx/move": ({ state, payload }) =>
      MapLayer(state, payload.id, (l) => ({
        ...l,
        fx: FxSlotMove(l.fx, payload.slot, payload.by),
      })),
    "layer/fx/bypass": ({ state, payload }) =>
      MapLayer(state, payload.id, (l) => ({ ...l, fx: FxSlotBypass(l.fx, payload.slot) })),
    "layer/fx/opts": ({ state, payload }) =>
      MapLayer(state, payload.id, (l) => ({
        ...l,
        fx: FxSlotOpts(l.fx, payload.slot, payload.opts),
      })),

    "fx/add": ({ state, payload }) => ({ ...state, fx: FxSlotAdd(state.fx, payload.type) }),
    "fx/remove": ({ state, payload }) => ({ ...state, fx: FxSlotRemove(state.fx, payload.slot) }),
    "fx/move": ({ state, payload }) => ({
      ...state,
      fx: FxSlotMove(state.fx, payload.slot, payload.by),
    }),
    "fx/bypass": ({ state, payload }) => ({ ...state, fx: FxSlotBypass(state.fx, payload.slot) }),
    "fx/opts": ({ state, payload }) => ({
      ...state,
      fx: FxSlotOpts(state.fx, payload.slot, payload.opts),
    }),
  };

  constructor(props: SampleGenProps = {}) {
    super(props, { ...SampleGenDefaultState, ...props.initial }, props.dispatcher);
  }
}
